import { useQuery } from '@tanstack/react-query'
import { useVisionClient } from '@/app/vision-client-provider'
import type { Channel, Tenant, Vendor } from '@/lib/vision/schemas'

/**
 * React-Query hooks for Vision's directory surface — the tenant switcher +
 * channel + vendor dropdowns pulse-v2 renders above the Purchase Orders table.
 *
 * Cache tuning:
 *   * `staleTime: 5 min`  — the underlying tables are hand-maintained by ops.
 *     Rows change once a partner is onboarded, which is a rare event on the
 *     order of days. A five-minute cache keeps the network noise near zero
 *     without silently masking a fresh-onboarded partner.
 *   * `gcTime: 30 min` (React Query default is 5) — a Chrome tab left open
 *     overnight shouldn't force a refetch on every idle → focus transition,
 *     the data doesn't move that fast.
 *
 * The three queries are DELIBERATELY not wrapped in one `useDirectory()` hook:
 * the tenant switcher lives in a different component tree than the channel /
 * vendor dropdowns (the switcher is in the top bar, the dropdowns are in the
 * orders route). Keeping them per-hook lets each mount / unmount independently
 * without forcing a fetch of siblings it doesn't render.
 *
 * Vendors take an optional channel scope, which becomes part of the cache key:
 * `['directory', 'vendors', tenantCode, channel, channelCode]`. Scoped and
 * unscoped answers are different lists, so they get different entries rather
 * than overwriting each other — switching channel is a key change and refetches
 * (showing the usual dropdown spinner), while the tenant-wide list callers
 * already hold stays cached and valid. The tuning above still applies to both:
 * which vendors trade on a channel moves on the same slow, ops-driven cadence
 * as the directory itself.
 */

const STALE_MS = 5 * 60 * 1000
const GC_MS = 30 * 60 * 1000

export function useTenantsQuery() {
  const client = useVisionClient()
  return useQuery<Tenant[]>({
    queryKey: ['directory', 'tenants'],
    queryFn: async () => {
      const response = await client.listTenants()
      return response.items
    },
    staleTime: STALE_MS,
    gcTime: GC_MS,
  })
}

export function useChannelsQuery(tenantCode: string) {
  const client = useVisionClient()
  return useQuery<Channel[]>({
    queryKey: ['directory', 'channels', tenantCode],
    queryFn: async () => {
      const response = await client.listChannels({ tenantCode })
      return response.items
    },
    enabled: !!tenantCode,
    staleTime: STALE_MS,
    gcTime: GC_MS,
  })
}

export interface VendorScope {
  channel?: string[] | null
  channelCode?: string | null
}

export function useVendorsQuery(tenantCode: string, scope?: VendorScope) {
  const client = useVisionClient()
  const channel = scope?.channel?.length ? scope.channel : null
  const channelCode = scope?.channelCode || null
  return useQuery<Vendor[]>({
    queryKey: ['directory', 'vendors', tenantCode, channel, channelCode],
    queryFn: async () => {
      const response = await client.listVendors({ tenantCode, channel, channelCode })
      return response.items
    },
    enabled: !!tenantCode,
    staleTime: STALE_MS,
    gcTime: GC_MS,
  })
}
